import { useMemo, useState } from 'react';
import { AssetList, assetsClipDestroy } from '@goldcast/api/content';
import { useMutation } from 'react-query';
import { Clip as Recording } from '@goldcast/player-config/src/types/clip-types';
import { ClipPublishedStatus } from '@goldcast/player-config/src/types/asset-types';
import RecordingRow from './RecordingRow';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import ClipUsageDialog from '@/components/molecules/ClipPublish/ClipUsageDialog';
import useAssetUsage from '@/hooks/useAssetUsage';
import { useAppContext } from '@/context/AppContext/AppContext';
import useDialog from '@/components/organisms/useDialog';
import useDownloadAsset from '@/hooks/useDownloadAsset';
import useClipNavigate from '@/hooks/useClipNavigate';
import useAnalytics from '@/hooks/useAnalytics';
import { showErrorToast } from '@/libs/toast/toast';

interface DuplicateRecordingRowProps {
  recording: Recording;
  isActive: boolean;
  onSelect: () => void;
  onDeleted: (recordingId: string) => void;
}

/**
 * A single duplicate recording row in the recordings dropdown, wired for its two actions:
 * - Download reuses `useDownloadAsset` (the same clip-download flow used across the app); when the
 *   duplicate isn't fully loaded yet it falls back to opening the clip with download intent.
 * - Delete calls `assetsClipDestroy` behind a confirmation, then lets the parent update the store
 *   and route via `onDeleted`. It follows the same rule as deleting a clip from the editor's clip
 *   library: a published duplicate can be deleted, unless it's published to the video library and
 *   in use somewhere — then the usage dialog lists where, and the delete is blocked.
 *
 * The original recording never carries these actions, so it is rendered by the dropdown as a plain
 * `RecordingRow`; only duplicates go through this container.
 */
export default function DuplicateRecordingRow({
  recording,
  isActive,
  onSelect,
  onDeleted
}: DuplicateRecordingRowProps) {
  const { isOpen: isConfirmOpen, openDialog: openConfirm, closeDialog: closeConfirm } = useDialog();
  const { navigateToClip } = useClipNavigate();
  const { trackRecordingDuplicate } = useAnalytics();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState(false);
  const { adminAppStore } = useAppContext();
  const { usageData: clipAssetUsage, checkAssetUsage } = useAssetUsage();
  const {
    isOpen: isClipUsageDialogOpen,
    openDialog: openClipUsageDialog,
    closeDialog: closeClipUsageDialog
  } = useDialog();

  const isVideoLibraryClipPublishEnabled = useMemo(
    () => adminAppStore.getters?.['org/getCurrentOrg']?.showVideoLibraryClipPublish || false,
    [adminAppStore]
  );

  const isPublished = recording.asset_metadata?.publish_status === ClipPublishedStatus.PUBLISHED;

  const { onDownloadClip } = useDownloadAsset(
    { type: 'CLIP', clip: recording as unknown as AssetList },
    navigateToClip
  );

  const { mutate: deleteClip, isLoading: isDeleting } = useMutation(assetsClipDestroy, {
    onSuccess: () => {
      trackRecordingDuplicate({ action: 'Delete', recordingId: recording.id });
      closeConfirm();
      onDeleted(recording.id);
    },
    onError: (err: any) => {
      let message = 'Failed to delete duplicate. Please try again.';
      try {
        message = JSON.parse(err)?.error ?? message;
      } catch {
        // Non-JSON errors keep the default message.
      }
      showErrorToast(message);
    }
  });

  const handleDownload = () => {
    trackRecordingDuplicate({ action: 'Download', recordingId: recording.id });
    setIsDownloading(true);
    onDownloadClip()
      // onDownloadClip rejects for already-handled cases (unverified email, or a missing config that
      // triggers a navigation instead); those surface their own feedback, so just swallow here.
      .catch(() => undefined)
      .finally(() => setIsDownloading(false));
  };

  // Same gate as the editor's clip library delete: block when the video library still uses the clip.
  const handleDelete = async () => {
    const videoLibraryId = recording.asset_metadata?.video_library_id;
    if (isVideoLibraryClipPublishEnabled && videoLibraryId) {
      setIsCheckingUsage(true);
      const usageCount = await checkAssetUsage(videoLibraryId).finally(() => setIsCheckingUsage(false));
      if (usageCount > 0) {
        openClipUsageDialog();
        return;
      }
    }
    openConfirm();
  };

  return (
    <>
      <RecordingRow
        id={recording.id}
        title={recording.title}
        isActive={isActive}
        isPublished={isPublished}
        onSelect={onSelect}
        onDownload={handleDownload}
        onDelete={handleDelete}
        isDownloading={isDownloading}
        isDeleting={isDeleting || isCheckingUsage}
      />
      <ContentDialog
        isOpen={isClipUsageDialogOpen}
        hideCloseIcon={true}
        size="medium"
        setIsOpen={closeClipUsageDialog}
        disableBackdropClose={false}
      >
        <ClipUsageDialog onClose={closeClipUsageDialog} clipAssetUsage={clipAssetUsage} type={'DELETE'} />
      </ContentDialog>
      <ConfirmationDialog
        isOpen={isConfirmOpen}
        onClose={closeConfirm}
        onConfirm={() => deleteClip({ id: recording.id })}
        title="Delete Duplicate"
        content="Are you sure you want to delete this duplicate? This will permanently delete the duplicate recording and cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmLoading={isDeleting}
      />
    </>
  );
}
